import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import CloseIcon from '../../assets/CloseIcon'
import {
    HEADER_CONFIG,
    HEADER_CONST,
    SIDEBAR_CONST
} from '../../common/constant/common-constant'
import { 
    PAGE_CONST,
    HOME_CONST,
    HOME_NAVIGATION_ITEMS,
    PERMISSION_TYPE
} from '../../common/constant/page-constant'
import Header from '../Common/Header/Header'
import MobileHeader from '../Common/Header/MobileHeader'
import SideMenu from '../Common/SideMenu/SideMenu'
import useCheckMobileScreen from '../../common/hooks/use-check-mobile-screen-hook'
import { formatDateTime } from '../../common/utils/appUtils'
import {
    getCustomerSalesOrdersHelper,
    getSalesOrderHelper
} from '../../helper/home-helper'

import '../Home/Home.scss'
import '../HomeDetails/HomeDetails.scss'
import './CustomerDetails.scss'

const CustomerDetails = props => {
    const navigate = useNavigate()
    const location = useLocation()
    const isMobile = useCheckMobileScreen()
    
    // Get customer data from location state
    const customer = location.state?.customer
    
    const [headerOptions, setHeaderOptions] = useState({
        [HEADER_CONFIG.IS_BACK_VISIBLE]: true,
        [HEADER_CONFIG.TITLE]: 'Customer Details',
        [HEADER_CONFIG.HANDLE_BACK_FUNC]: () => navigate(-1)
    })
    
    // Permissions
    const [permissions, setPermissions] = useState({})
    
    // Purchase history states
    const [purchaseHistory, setPurchaseHistory] = useState([])
    const [loading, setLoading] = useState(true)
    
    // Date range states - default to last 3 months
    const today = new Date()
    const threeMonthsAgo = new Date()
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3)
    
    const [startDate, setStartDate] = useState(threeMonthsAgo.toISOString().split('T')[0])
    const [endDate, setEndDate] = useState(today.toISOString().split('T')[0])
    
    // Invoice details states
    const [selectedInvoice, setSelectedInvoice] = useState(null)
    const [invoiceDetails, setInvoiceDetails] = useState(null)
    const [loadingInvoice, setLoadingInvoice] = useState(false)

    useEffect(() => {
        if (!customer) {
            navigate(-1)
            return
        }
        fetchPurchaseHistory()
    }, [customer])

    useEffect(() => {
        if (props.loginData?.permissions) {
            let permission = props.loginData.permissions.reduce(
                (acc, permission) => {
                    acc.features = {
                        ...acc.features,
                        ...permission.features
                    }
                    return acc
                },
                {}
            )
            if (permission) setPermissions(permission)
        }
    }, [props.loginData])

    const fetchPurchaseHistory = () => {
        if (!customer) return
        
        setLoading(true)
        getCustomerSalesOrdersHelper(customer.id, startDate, endDate)
            .then(res => {
                setPurchaseHistory(res)
                setLoading(false)
            })
            .catch(err => {
                console.log(err)
                setPurchaseHistory([])
                setLoading(false)
            })
    }

    const handleDateRangeChange = () => {
        fetchPurchaseHistory()
    }

    const handleInvoiceClick = (order) => {
        setSelectedInvoice(order)
        setLoadingInvoice(true)
        
        getSalesOrderHelper(order.id)
            .then(res => {
                setInvoiceDetails(res)
                setLoadingInvoice(false)
            })
            .catch(err => {
                console.log(err)
                setInvoiceDetails(null)
                setLoadingInvoice(false)
            })
    }

    const closeInvoiceDetails = () => {
        setSelectedInvoice(null)
        setInvoiceDetails(null)
    }

    const handleBack = () => {
        navigate('/home/details')
    }

    const handleNavItemClick = (itemName) => {
        navigate('/home/details')
    }

    if (!customer) {
        return null
    }

    return (
        <div className="containerHome">
            {!isMobile && <Header />}
            {isMobile && <MobileHeader headerOptions={headerOptions} />}
            <SideMenu activeItem={SIDEBAR_CONST.HOME} component="HOME_DETAILS" />
            <div className="pageBody dFlex">
                {!isMobile && (
                    <div className="pageBodyLeftPanel sticky">
                        <div className="chainNavigationWrapper">
                            <ul className="chainNavigation">
                                {HOME_NAVIGATION_ITEMS.map(item => {
                                    if (permissions?.features?.[item.feature] !== PERMISSION_TYPE.RESTRICTED) {
                                        return (
                                            <li
                                                key={item.name}
                                                className={`chainItem ${item.name === HOME_CONST.CUSTOMERS ? 'active' : ''}`}
                                                onClick={() => handleNavItemClick(item.name)}
                                            >
                                                {item.homeDetailIcon}
                                                <span>{item.name}</span>
                                            </li>
                                        )
                                    }
                                    return null
                                })}
                            </ul>
                        </div>
                    </div>
                )}
                <div className="pageBodyRightPanel customerDetailsPanel">
                    {/* Header */}
                    <div className="pageBodyRightPanelHeader dFlex jcSpaceBetween aiCenter">
                        <div className="pageBodyRightPanelHeading">
                            Customer Details
                        </div>
                        <div className="addProductBtn dFlex aiCenter">
                            <div
                                className="pointer ml-4"
                                onClick={handleBack}>
                                <CloseIcon />
                            </div>
                        </div>
                    </div>

                    {/* Customer Information Card */}
                    <div className="customerInfoCard">
                        <div className="customerAvatar">
                            {customer.name?.[0]?.toUpperCase()}
                        </div>
                        <div className="customerInfo">
                            <h2 className="customerName">{customer.name}</h2>
                            <p className="customerPhone">{customer.mobile_no}</p>
                            <p className="customerAddress">{customer.billing_address}</p>
                        </div>
                    </div>

                    {/* Date Range Filter */}
                    <div className="dateRangeFilter">
                        <h3>Purchase History</h3>
                        <div className="dateInputs">
                            <div className="dateField">
                                <label>From</label>
                                <input 
                                    type="date" 
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                />
                            </div>
                            <div className="dateField">
                                <label>To</label>
                                <input 
                                    type="date" 
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                />
                            </div>
                            <button className="btn btnPrimary" onClick={handleDateRangeChange}>
                                Apply
                            </button>
                        </div>
                    </div>

                    {/* Purchase History Table */}
                    <div className="purchaseHistorySection">
                        {loading ? (
                            <div className="loadingState">Loading purchase history...</div>
                        ) : purchaseHistory.length === 0 ? (
                            <div className="emptyState">No purchase history found for the selected date range.</div>
                        ) : (
                            <div className="tableWrapper">
                                <table className="purchaseHistoryTable">
                                    <thead>
                                        <tr>
                                            <th>Invoice No.</th>
                                            <th>Date & Time</th>
                                            <th>Sales Type</th>
                                            <th>Total MRP</th>
                                            <th>Discount BDT (-)</th>
                                            <th>Exc/Ret (-)</th>
                                            <th>Net Amt BDT</th>
                                            <th>Payment Mode</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {purchaseHistory.map((order, index) => (
                                            <tr 
                                                key={order.id || index}
                                                onClick={() => handleInvoiceClick(order)}
                                                className="clickableRow"
                                            >
                                                <td className="invoiceNo">{order.invoice_number}</td>
                                                <td>{order.created_at ? formatDateTime(order.created_at) : '-'}</td>
                                                <td>
                                                    <span className={`salesType ${order.type?.toLowerCase()}`}>
                                                        {order.type === 'IN_STORE' ? 'In Store' : order.type}
                                                    </span>
                                                </td>
                                                <td>৳ {(order.amount || 0).toFixed(2)}</td>
                                                <td className="discount">৳ {(order.discount || 0).toFixed(2)}</td>
                                                <td className="excRet">৳ {(order.exchange_return_amount || 0).toFixed(2)}</td>
                                                <td className="netAmt">৳ {(order.net_amount || 0).toFixed(2)}</td>
                                                <td>
                                                    <span className={`paymentMode ${order.payment_mode?.toLowerCase()}`}>
                                                        {order.payment_mode || '-'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Invoice Details Modal */}
                    {selectedInvoice && (
                        <div className="invoiceDetailsModal">
                            <div className="modalOverlay" onClick={closeInvoiceDetails}></div>
                            <div className="modalContent">
                                <div className="modalHeader">
                                    <h3>Invoice Details - {selectedInvoice.invoice_number}</h3>
                                    <button className="closeModalBtn" onClick={closeInvoiceDetails}>
                                        <CloseIcon />
                                    </button>
                                </div>
                                <div className="modalBody">
                                    {loadingInvoice ? (
                                        <div className="loadingState">Loading invoice details...</div>
                                    ) : invoiceDetails ? (
                                        <>
                                            <div className="invoiceSummary">
                                                <div className="summaryItem">
                                                    <span className="label">Date:</span>
                                                    <span className="value">{formatDateTime(invoiceDetails.created_at)}</span>
                                                </div>
                                                <div className="summaryItem">
                                                    <span className="label">Type:</span>
                                                    <span className="value">{invoiceDetails.type === 'IN_STORE' ? 'In Store' : invoiceDetails.type}</span>
                                                </div>
                                                <div className="summaryItem">
                                                    <span className="label">Payment Mode:</span>
                                                    <span className="value">{invoiceDetails.payment_mode || '-'}</span>
                                                </div>
                                            </div>
                                            
                                            <h4>Purchased Products</h4>
                                            <div className="productTableWrapper">
                                                <table className="productTable">
                                                    <thead>
                                                        <tr>
                                                            <th>Product Name</th>
                                                            <th>Dose</th>
                                                            <th>Qty</th>
                                                            <th>Unit Price</th>
                                                            <th>Total</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {invoiceDetails.product_details?.map((product, index) => (
                                                            <tr key={product.id || index}>
                                                                <td>{product.name}</td>
                                                                <td>{product.dose || '-'}</td>
                                                                <td>{product.quantity}</td>
                                                                <td>৳ {(product.rate_per_unit || 0).toFixed(2)}</td>
                                                                <td>৳ {(product.final_price || 0).toFixed(2)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                            
                                            <div className="invoiceTotals">
                                                <div className="totalRow">
                                                    <span>Subtotal:</span>
                                                    <span>৳ {(invoiceDetails.amount || 0).toFixed(2)}</span>
                                                </div>
                                                <div className="totalRow">
                                                    <span>Discount:</span>
                                                    <span>- ৳ {(invoiceDetails.discount || 0).toFixed(2)}</span>
                                                </div>
                                                <div className="totalRow">
                                                    <span>VAT:</span>
                                                    <span>৳ {(invoiceDetails.vat || 0).toFixed(2)}</span>
                                                </div>
                                                <div className="totalRow grandTotal">
                                                    <span>Grand Total:</span>
                                                    <span>৳ {(invoiceDetails.total_amount || 0).toFixed(2)}</span>
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="emptyState">Unable to load invoice details.</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

const mapStateToProps = state => {
    return {
        loginData: state.loginDataReducer?.loginData
    }
}

const mapDispatchToProps = dispatch => {
    return {}
}

export default connect(mapStateToProps, mapDispatchToProps)(CustomerDetails)
